// Missed-dose backfill (MISSED_DOSE_BACKFILL_PLAN.md). Pure data — safe for API + web.

import type { MedUnit, MedIngredient, MedRoute, ScheduledGroup } from "./medication.js";

/** One expected-but-unlogged dose from the past 7 days. */
export interface BackfillSlot {
  /** Deterministic id: `${normalizedName}|${dayKey}|${anchorHM}` (e.g. "lisinopril|2026-07-09|08:00"). */
  slotId: string;
  name: string;                    // display name as saved on the item / last dose
  dose?: number;
  unit?: MedUnit;
  ingredients?: MedIngredient[];
  strength: string;                // formatMedStrength() output for display
  route: MedRoute;
  scheduledGroup: ScheduledGroup;
  /** Where the expectation came from. */
  source: "schedule" | "regimen" | "learned";
  /** Present for source "schedule"/"regimen" — used by the POST to re-derive the payload. */
  regimenId?: string;
  lineId?: string;
  /** Display name of the owning regimen (e.g. "Morning Pills"), when regimenId is present. */
  regimenName?: string;
  /** The expected instant (UTC ISO) — default takenAt for "Took it" and fixed takenAt for "Didn't take it". */
  anchorIso: string;
  /** Local calendar day of the anchor, "YYYY-MM-DD" in the patient's tz. */
  dayKey: string;
  /** Local clock time of the anchor, "HH:MM". */
  anchorLocalTime: string;
}

export interface BackfillSlotsResponse {
  slots: BackfillSlot[];   // newest day first, then by anchor time ascending within a day
  generatedAt: string;
}

/** One user decision sent to POST /medications/backfill/log. */
export interface BackfillLogEntry {
  slotId: string;
  /** Echoed from the slot so the server can re-derive the payload. */
  regimenId?: string;
  lineId?: string;
  name: string;            // required for learned slots (no regimen line); server matches history by this
  /** UTC ISO. For "Took it": user-adjusted or the anchor. For missed: the anchor. */
  takenAt: string;
  /** true = "Didn't take it" (records medication_taken with missed:true). */
  missed: boolean;
}

export const BACKFILL_LOOKBACK_DAYS = 7;
export const BACKFILL_TOLERANCE_HOURS = 4;
/** Sanity floor for a user-chosen custom "taken at" time on a confirm (Log medication
 *  dialog / suggestion card). Deliberately far wider than BACKFILL_LOOKBACK_DAYS — that
 *  7-day window only bounds the missed-dose sweep. This just catches year typos. */
export const CUSTOM_TAKEN_AT_MAX_DAYS_BACK = 365 * 5;
/** Forward slack for a custom "taken at" time, to absorb client clock skew. */
export const CUSTOM_TAKEN_AT_FUTURE_SLACK_MS = 3_600_000;
// Raised from 40/30: the sweep now emits slots per (medication, day-part) rather
// than per medication, so a household with meds scheduled at multiple day-parts
// (e.g. a morning AND a bedtime regimen sharing pantoprazole) legitimately
// produces roughly twice as many rows across the 7-day window. Newest day sorts
// first, so "yesterday" is never the row that gets truncated.
export const MAX_BACKFILL_SLOTS = 60;
export const MAX_BACKFILL_LOG_ENTRIES = 60;

/** Context captured when rating the "Was this catch-up list accurate?" control inside
 *  the Missed Doses review modal (MissedDoseBackfill.tsx). Client-attached as
 *  pendingProposal so a rating carries the exact list + in-progress decisions the user
 *  was looking at, mirroring PtSheetScanFeedbackContext / ConditionReportFeedbackContext. */
export interface MissedDoseBackfillFeedbackContext {
  kind: "missed_dose_backfill";
  /** The slots on screen at rating time (server response, unmodified). */
  slots: BackfillSlot[];
  /** The user's in-progress "Took it" / "Didn't take" decisions, if any were staged. */
  staged: Array<{ slotId: string; missed: boolean; takenAt: string }>;
  /** Medications the user added to a group that weren't on the list (e.g. a substitute
   *  for a pill they skipped), each staged as taken. Absent on older clients. */
  added?: Array<{ name: string; regimenId: string; lineId: string; takenAt: string }>;
}

/**
 * Server-computed snapshot of the missed-dose backfill sweep's live state for a patient
 * at feedback time. Independent of the client-attached pendingProposal above — this is a
 * fresh recompute of buildBackfillSlots() plus the raw recent medication history it read
 * from, so a reviewer can see exactly why a slot was (or wasn't) flagged as missed even
 * when the client-attached slots list is stale or absent.
 */
export interface BackfillDebug {
  now: string; // ISO instant the sweep evaluated at
  timezone: string;
  toleranceHours: number;
  lookbackDays: number;
  /** Fresh recompute of the sweep — the slots it would currently report as missed. */
  slots: BackfillSlot[];
  /** Recent medication_taken history the sweep read from (newest first), for cross-checking
   *  a specific slot's fill math (computeDayFills) by hand. */
  recentDoses: Array<{ name: string; occurredAt: string; missed?: boolean }>;
}
